<template>
    <PageSidebar :showError="v$.$error">
        <template #sidebarTitle> {{ $t("love.menu.request_review") }} </template>
        <template #sidebarMain>
            <CardToggle :title="$t('love.form.content')">
                <NotificationInfo :closeButton="false">
                    {{ $t("love.reviews.change_fields_info") }}
                    <span @click="goToForm" class="cursor-pointer underline">{{
                        $t("love.reviews.change_fields_info_link")
                    }}</span
                    >.
                </NotificationInfo>
            </CardToggle>
            <CardToggle :title="$t('love.form.send_request_to')">
                <div class="space-y-5">
                    <FormTextArea
                        v-model="emails"
                        @blur="v$.emails.$touch"
                        :title="$t('love.reviews.emails')"
                        :help="$t('love.reviews.email_info')"
                        :error="v$.emails.$errors?.[0]?.$message"
                    />
                </div>
            </CardToggle>
            <div class="p-6 space-y-5">
                <ButtonPrimaryMd
                    class="lg:hidden w-full"
                    :disabled="requestSent"
                    @click="handleSendRequest()"
                >
                    {{ $t("love.reviews.send_request") }}
                </ButtonPrimaryMd>
            </div>
        </template>
        <template #sidebarFooter>
            <cErrorInfo v-if="v$.$error" />
            <cSaveButton
                @save="handleSendRequest"
                :disabled="requestSent"
                :ctaEnabled="$t('love.reviews.send_request')"
                :ctaDisabled="$t('love.reviews.send_request')"
            />
        </template>
    </PageSidebar>
</template>

<script>
import { LOVE_PREVIEW_KIND, LOVE_ROUTE_NAMES } from "@/apps/love/love.routes";
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { makeFileModel, FormTextArea } from "@easy/ui/forms";
import { ButtonPrimaryMd } from "@easy/ui/buttons";
import { required, emailList } from "@/shared/utils/forms/i18n-vuelidators";
import { useFormValidation } from "@/shared/utils/forms/useFormValidation";
import { CardToggle } from "@easy/ui/cards";
import { NotificationInfo } from "@easy/ui/notifications";
import cErrorInfo from "@/shared/components/cErrorInfo.vue";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    components: {
        PageSidebar,
        FormTextArea,
        ButtonPrimaryMd,
        CardToggle,
        NotificationInfo,
        cErrorInfo,
        cSaveButton,
    },
    setup() {
        useFocusOnQueryField();
        const { v$, validateForm, resetFrom } = useFormValidation();
        return { v$, validateForm, resetFrom };
    },
    validations() {
        const rules = {
            emails: { required, emailList },
            // this is a hack to stop vuelidate from making dirty when emails is touched
            emptyRule: { required },
        };
        return rules;
    },
    data() {
        return {
            emails: "",
            emptyRule: "empty",
            files: [],
            requestSent: false,
        };
    },
    computed: {
        ...mapState("love", ["wall", "hasChanges"]),
        listOfEmails() {
            return this.emails.split(",").map((email) => email.toLowerCase().trim());
        },
    },
    watch: {
        wall: {
            handler(wall, oldWall) {
                if (!oldWall?.askImg && wall?.askImg) {
                    this.files = [
                        makeFileModel({
                            url: wall.askImg,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        ...mapActions("love", [
            "uploadAuthorAvatar",
            "uploadAskImage",
            "sendCreateRequest2",
            "hostCode",
        ]),
        ...mapActions("alert", ["showSuccessMessage"]),
        async handleSendRequest() {
            const isOk = await this.validateForm();
            if (!isOk) {
                return;
            }
            this.requestSent = true;

            const published = await this.hostCode(false);
            if (!published) {
                this.requestSent = false;
                return;
            }

            const result = await this.sendCreateRequest2({ emails: this.listOfEmails });
            this.requestSent = false;
            if (result) {
                this.emails = "";
                await this.resetFrom();
            }
        },
        async uploadImg(file) {
            this.wall.askImg = await this.uploadAskImage({ file });
        },
        removeImg() {
            this.wall.askImg = "";
        },
        changeAuthorAvatar(file) {
            if (file === null) {
                this.wall.author.avatar = undefined;
            } else {
                this.uploadAuthorAvatar({ file });
            }
        },
        goToForm() {
            this.$router.push({
                name: LOVE_ROUTE_NAMES.projectForm,
                query: { view: LOVE_PREVIEW_KIND.mail },
            });
        },
    },
};
</script>
